import { Button } from "../../ui/components/Button";
import { Tooltip, TooltipTrigger } from "../../ui/components/Tooltip";
import { Maximize, Minus, Plus, Search, Shrink } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

interface ZoomableImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  maxWidth?: string;
  maxHeight?: string;
  fillContainer?: boolean;
  onImageDataReady?: (imageData: ImageData) => void;
}

interface Transform {
  scale: number;
  x: number;
  y: number;
}

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 3.0;
const ZOOM_STEP = 0.05;

export function ZoomableImage({
  src,
  alt,
  className = "",
  style = {},
  maxWidth = "88vw",
  maxHeight = "77vh",
  fillContainer = false,
  onImageDataReady,
}: ZoomableImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const hiddenImageRef = useRef<HTMLImageElement>(null);
  const hasExtractedHistogramData = useRef(false);

  // Core state
  const [transform, setTransform] = useState<Transform>({ scale: 1, x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Dual image state for smooth transitions
  const [currentSrc, setCurrentSrc] = useState(src);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Interaction state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, transformX: 0, transformY: 0 });

  // Pinch gesture state
  const [isPinching, setIsPinching] = useState(false);
  const pinchStartRef = useRef<{
    distance: number;
    centerX: number;
    centerY: number;
    scale: number;
    x: number;
    y: number;
  } | null>(null);

  // Refs for stable access in event handlers
  const transformRef = useRef(transform);
  const isDraggingRef = useRef(isDragging);
  const isPinchingRef = useRef(isPinching);
  const dragStartRef = useRef(dragStart);

  useEffect(() => {
    transformRef.current = transform;
    isDraggingRef.current = isDragging;
    isPinchingRef.current = isPinching;
    dragStartRef.current = dragStart;
  });

  // Baseline fit scale (what "fit to screen" returns to)
  const [baselineScale, setBaselineScale] = useState(1);

  // Calculate the scale that makes the image fit exactly in the container
  const calculateContainerFitScale = useCallback(() => {
    if (
      !naturalSize.width ||
      !naturalSize.height ||
      !containerSize.width ||
      !containerSize.height
    ) {
      return 1;
    }

    const scaleX = containerSize.width / naturalSize.width;
    const scaleY = containerSize.height / naturalSize.height;
    return fillContainer ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
  }, [naturalSize, containerSize, fillContainer]);

  // Handle main image load - get natural dimensions (for initial load only)
  const handleImageLoad = useCallback(() => {
    if (imageRef.current && currentSrc === src) {
      const { naturalWidth, naturalHeight } = imageRef.current;
      setNaturalSize({ width: naturalWidth, height: naturalHeight });
      setImageLoaded(true);

      // Extract ImageData for histogram only once (on first load)
      // This prevents flickering when preview updates
      if (onImageDataReady && !hasExtractedHistogramData.current) {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (ctx) {
            canvas.width = naturalWidth;
            canvas.height = naturalHeight;
            ctx.drawImage(imageRef.current, 0, 0);
            const imageData = ctx.getImageData(0, 0, naturalWidth, naturalHeight);
            onImageDataReady(imageData);
            hasExtractedHistogramData.current = true;
          }
        } catch (error) {
          console.warn("Failed to extract ImageData for histogram:", error);
        }
      }
    }
  }, [currentSrc, src, onImageDataReady]);

  // Update container size when container resizes
  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateContainerSize();
    window.addEventListener("resize", updateContainerSize);
    return () => window.removeEventListener("resize", updateContainerSize);
  }, []);

  // Handle src changes with dual image loading
  useEffect(() => {
    if (src === currentSrc) return; // No change needed

    if (!hiddenImageRef.current) return; // Safety check

    const handleHiddenLoad = () => {
      if (hiddenImageRef.current) {
        const { naturalWidth, naturalHeight } = hiddenImageRef.current;

        // Update natural size from the new image
        setNaturalSize({ width: naturalWidth, height: naturalHeight });

        // Extract ImageData for histogram if callback provided
        if (onImageDataReady) {
          try {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            if (ctx) {
              canvas.width = naturalWidth;
              canvas.height = naturalHeight;
              ctx.drawImage(hiddenImageRef.current, 0, 0);
              const imageData = ctx.getImageData(0, 0, naturalWidth, naturalHeight);
              onImageDataReady(imageData);
            }
          } catch (error) {
            console.warn("Failed to extract ImageData for histogram:", error);
          }
        }

        // Swap to new image
        setCurrentSrc(src);
        setImageLoaded(true);
        setIsFirstLoad(false);
      }
    };

    const handleHiddenError = () => {
      console.error("Failed to load new image:", src);
    };

    // Start loading new image in hidden element
    const hiddenImg = hiddenImageRef.current;
    if (hiddenImg) {
      hiddenImg.onload = handleHiddenLoad;
      hiddenImg.onerror = handleHiddenError;
      hiddenImg.src = src;
    }

    return () => {
      if (hiddenImg) {
        hiddenImg.onload = null;
        hiddenImg.onerror = null;
      }
    };
  }, [src, currentSrc, onImageDataReady]);

  // Reset image loaded state when currentSrc changes
  useEffect(() => {
    if (currentSrc !== src) {
      setImageLoaded(false);
    }
  }, [currentSrc, src]);

  // Set initial transform when image loads and container size is known
  useEffect(() => {
    if (imageLoaded && containerSize.width > 0 && containerSize.height > 0) {
      const fitScale = calculateContainerFitScale();
      setBaselineScale(fitScale);

      // Only reset transform on first load, not on src changes
      if (isFirstLoad) {
        // Always default to shrink to fit on initial load (show entire image)
        const scaleX = containerSize.width / naturalSize.width;
        const scaleY = containerSize.height / naturalSize.height;
        const shrinkScale = Math.min(scaleX, scaleY);

        setTransform({ scale: shrinkScale, x: 0, y: 0 });
        setIsFirstLoad(false);
      }
    }
  }, [imageLoaded, containerSize, calculateContainerFitScale, naturalSize, isFirstLoad]);

  // Mouse wheel zoom - using native event listener to allow preventDefault
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();

      if (!containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left - containerSize.width / 2;
      const mouseY = e.clientY - rect.top - containerSize.height / 2;

      // Much less sensitive zoom calculation
      const zoomIntensity = 0.01;
      const wheel = e.deltaY < 0 ? 1 : -1;
      const zoom = Math.exp(wheel * zoomIntensity);

      const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, transform.scale * zoom));

      if (Math.abs(newScale - transform.scale) < 0.001) {
        return;
      }

      // Calculate new position to zoom toward mouse
      const factor = newScale / transform.scale - 1;
      const newX = transform.x - mouseX * factor;
      const newY = transform.y - mouseY * factor;

      setTransform({ scale: newScale, x: newX, y: newY });
    },
    [transform, containerSize]
  );

  // Add native wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  // Mouse drag start
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) {
        return;
      }

      setIsDragging(true);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        transformX: transform.x,
        transformY: transform.y,
      });
    },
    [transform]
  );

  // Mouse drag move with reduced sensitivity
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) {
        return;
      }

      const deltaX = (e.clientX - dragStart.x) * 0.8; // Reduce pan sensitivity
      const deltaY = (e.clientY - dragStart.y) * 0.8;

      // Only update if movement is significant enough
      if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) {
        return;
      }

      setTransform((prev) => ({
        ...prev,
        x: dragStart.transformX + deltaX,
        y: dragStart.transformY + deltaY,
      }));
    },
    [isDragging, dragStart]
  );

  // Mouse drag end
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Native touch event handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const calculateDistance = (touch1: Touch, touch2: Touch): number => {
      const dx = touch2.clientX - touch1.clientX;
      const dy = touch2.clientY - touch1.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const calculateCenter = (touch1: Touch, touch2: Touch): { x: number; y: number } => {
      return {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0];
        const current = transformRef.current;
        setIsDragging(true);
        setIsPinching(false);
        setDragStart({
          x: touch.clientX,
          y: touch.clientY,
          transformX: current.x,
          transformY: current.y,
        });
      } else if (e.touches.length === 2) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = calculateDistance(touch1, touch2);
        const center = calculateCenter(touch1, touch2);
        const current = transformRef.current;

        setIsDragging(false);
        setIsPinching(true);
        pinchStartRef.current = {
          distance,
          centerX: center.x,
          centerY: center.y,
          scale: current.scale,
          x: current.x,
          y: current.y,
        };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Handle transition from 1 finger to 2 fingers
      if (e.touches.length === 2 && isDraggingRef.current && !isPinchingRef.current) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = calculateDistance(touch1, touch2);
        const center = calculateCenter(touch1, touch2);
        const current = transformRef.current;

        setIsDragging(false);
        setIsPinching(true);
        pinchStartRef.current = {
          distance,
          centerX: center.x,
          centerY: center.y,
          scale: current.scale,
          x: current.x,
          y: current.y,
        };
        return;
      }

      if (e.touches.length === 1 && isDraggingRef.current && !isPinchingRef.current) {
        e.preventDefault();
        const touch = e.touches[0];
        const start = dragStartRef.current;
        const deltaX = (touch.clientX - start.x) * 0.8;
        const deltaY = (touch.clientY - start.y) * 0.8;

        if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;

        setTransform({
          scale: transformRef.current.scale,
          x: start.transformX + deltaX,
          y: start.transformY + deltaY,
        });
      } else if (e.touches.length === 2 && isPinchingRef.current && pinchStartRef.current) {
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = calculateDistance(touch1, touch2);
        const currentCenter = calculateCenter(touch1, touch2);

        const pinchStart = pinchStartRef.current;
        const scaleFactor = currentDistance / pinchStart.distance;
        const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, pinchStart.scale * scaleFactor));

        const rect = container.getBoundingClientRect();
        const relativeCenterX = pinchStart.centerX - rect.left - rect.width / 2;
        const relativeCenterY = pinchStart.centerY - rect.top - rect.height / 2;

        const factor = newScale / pinchStart.scale - 1;
        let newX = pinchStart.x - relativeCenterX * factor;
        let newY = pinchStart.y - relativeCenterY * factor;

        const centerDeltaX = currentCenter.x - pinchStart.centerX;
        const centerDeltaY = currentCenter.y - pinchStart.centerY;
        newX += centerDeltaX;
        newY += centerDeltaY;

        setTransform({ scale: newScale, x: newX, y: newY });
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) {
        setIsDragging(false);
        setIsPinching(false);
        pinchStartRef.current = null;
      } else if (e.touches.length === 1 && isPinchingRef.current) {
        const touch = e.touches[0];
        const current = transformRef.current;
        setIsPinching(false);
        setIsDragging(true);
        setDragStart({
          x: touch.clientX,
          y: touch.clientY,
          transformX: current.x,
          transformY: current.y,
        });
        pinchStartRef.current = null;
      }
    };

    container.addEventListener("touchstart", handleTouchStart, { passive: false });
    container.addEventListener("touchmove", handleTouchMove, { passive: false });
    container.addEventListener("touchend", handleTouchEnd, { passive: false });
    container.addEventListener("touchcancel", handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, []);

  // Zoom controls
  const zoomIn = useCallback(() => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.min(MAX_ZOOM, prev.scale + ZOOM_STEP),
    }));
  }, []);

  const zoomOut = useCallback(() => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.max(MIN_ZOOM, prev.scale - ZOOM_STEP),
    }));
  }, []);

  const resetToFit = useCallback(() => {
    setTransform({ scale: baselineScale, x: 0, y: 0 });
  }, [baselineScale]);

  const zoom100 = useCallback(() => {
    setTransform({ scale: 1, x: 0, y: 0 });
  }, []);

  const shrinkToFit = useCallback(() => {
    if (
      !naturalSize.width ||
      !naturalSize.height ||
      !containerSize.width ||
      !containerSize.height
    ) {
      return;
    }
    const scaleX = containerSize.width / naturalSize.width;
    const scaleY = containerSize.height / naturalSize.height;
    const shrinkScale = Math.min(scaleX, scaleY);

    // Since the container uses flex centering, we just need to reset position
    setTransform({ scale: shrinkScale, x: 0, y: 0 });
  }, [naturalSize, containerSize]);

  const zoomPercentage = Math.round(transform.scale * 100);

  return (
    <div
      ref={containerRef}
      className={`relative flex items-center justify-center ${className}`}
      style={{
        ...style,
        maxWidth,
        maxHeight,
        cursor: isDragging ? "grabbing" : "grab",
        touchAction: "none",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Visible Image */}
      <img
        ref={imageRef}
        src={currentSrc}
        alt={alt}
        className="block max-h-none max-w-none"
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transformOrigin: "center center",
          transition: isDragging || isPinching ? "none" : "transform 0.3s ease-out",
          userSelect: "none",
          pointerEvents: "none",
          willChange: isDragging || isPinching ? "transform" : "auto",
        }}
        onLoad={handleImageLoad}
        draggable={false}
      />

      {/* Hidden Image for Preloading */}
      <img ref={hiddenImageRef} alt="" className="hidden" draggable={false} />

      {/* Zoom Controls - Top Center */}
      <div className="-translate-x-1/2 absolute top-4 left-1/2 flex items-center gap-1 rounded-lg border border-border/50 bg-background/90 px-3 py-1 shadow-sm backdrop-blur-sm">
        {/* Zoom Out */}
        <TooltipTrigger delay={300}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 border-none p-0 hover:bg-muted"
            onPress={zoomOut}
            isDisabled={transform.scale <= MIN_ZOOM}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <Tooltip>Zoom out</Tooltip>
        </TooltipTrigger>

        {/* Zoom Percentage */}
        <div className="min-w-[2.5rem] px-2 text-center font-medium text-xs">{zoomPercentage}%</div>

        {/* Zoom In */}
        <TooltipTrigger delay={300}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 border-none p-0 hover:bg-muted"
            onPress={zoomIn}
            isDisabled={transform.scale >= MAX_ZOOM}
          >
            <Plus className="h-3 w-3" />
          </Button>
          <Tooltip>Zoom in</Tooltip>
        </TooltipTrigger>

        {/* Separator */}
        <div className="mx-1 h-4 w-px bg-border" />

        {/* Fit to Screen */}
        <TooltipTrigger delay={300}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 border-none p-0 hover:bg-muted"
            onPress={resetToFit}
          >
            <Maximize className="h-3 w-3" />
          </Button>
          <Tooltip>Fit to screen</Tooltip>
        </TooltipTrigger>

        {/* Shrink to Fit */}
        <TooltipTrigger delay={300}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 border-none p-0 hover:bg-muted"
            onPress={shrinkToFit}
          >
            <Shrink className="h-3 w-3" />
          </Button>
          <Tooltip>Shrink to fit</Tooltip>
        </TooltipTrigger>

        {/* 100% Zoom */}
        <TooltipTrigger delay={300}>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 border-none p-0 hover:bg-muted"
            onPress={zoom100}
          >
            <Search className="h-3 w-3" />
          </Button>
          <Tooltip>100% zoom</Tooltip>
        </TooltipTrigger>
      </div>
    </div>
  );
}
